import React, { useEffect, useMemo, useState } from 'react';
import { Button, Space, Input, Form, Select, Row, Col, Tag, Typography, notification, DatePicker } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { TableCustom } from '../../../components/TableCustom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { deleteBlocksRequest, getListBlocksRequest } from '../../../services/onlinestore/blockServiceRequest';
import { Link, useHistory, useNavigate, useParams } from 'react-router-dom';
import { AppSelect } from '../../../constants/appSelect';
import { formatPrice, newObjectKeys } from '../../../utils/utility';
import { deleteMiniAppRequest, getListMiniAppsRequest } from '../../../services/onlinestore/miniAppsServiceRequest';
import { getListPartnersRequest } from '../../../services/onlinestore/partnerServiceRequest';
import { getListReconciliationRequest } from '../../../services/onlinestore/reconcileServiceRequest';
import dayjs from 'dayjs';

const { Text } = Typography;

const Reconciliation = () => {
  const navigate = useNavigate();
  const [formSearch] = Form.useForm();
  const { partnerId } = useParams();
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
    partner_id: [Number(partnerId)],
    start_time: dayjs().subtract(3, 'months').format('YYYY-MM-DD'),
    end_time: dayjs().format('YYYY-MM-DD'),
  });

  useEffect(() => {
    setParams({
      page: 1,
      limit: 20,
      partner_id: [Number(partnerId)],
      start_time: dayjs().subtract(3, 'months').format('YYYY-MM-DD'),
      end_time: dayjs().format('YYYY-MM-DD'),
    });
  }, [partnerId]);

  const {
    data: dataReconciliations,
    isPending,
    refetch: refetchDataReconciliations,
  } = useQuery({ queryKey: ['dataReconciliations', params], queryFn: () => getListReconciliationRequest(params) });

  // get data miniapps
  const { data: dataPartners } = useQuery({
    queryKey: ['dataPartners'],
    queryFn: () => getListPartnersRequest({ page: 1, limit: 1000, status: [1, 2] }),
  });

  // get data miniapps
  const { data: dataMiniApps } = useQuery({
    queryKey: ['dataMiniApps', partnerId],
    queryFn: () => getListMiniAppsRequest({ page: 1, limit: 1000, partner_id: [partnerId] }),
  });

  // convert list miniapps options,
  const dataMiniAppsOption = useMemo(() => {
    const options =
      dataMiniApps?.data?.map((item) => {
        return { label: item?.title, value: item?.id };
      }) || [];
    return [{ label: 'Tất cả', value: -1 }, ...options];
  }, [dataMiniApps]);

  const handleSubmitForm = (values) => {
    const temp = { ...params, page: 1 };
    if (values?.from_to_date) {
      Object.assign(temp, {
        start_time: dayjs(values?.from_to_date?.[0]).format('YYYY-MM-DD'),
        end_time: dayjs(values?.from_to_date?.[1]).format('YYYY-MM-DD'),
      });
    }

    if (values?.order_id) {
      Object.assign(temp, { order_id: values?.order_id });
    }

    if (values?.customer_name) {
      Object.assign(temp, { customer_name: values?.customer_name });
    }

    if (values?.miniapp_id >= 0) {
      Object.assign(temp, { miniapp_id: [values?.miniapp_id] });
    }

    if (values?.status) {
      Object.assign(temp, { status: [values?.status] });
    }

    setParams(temp);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      minWidth: 50,
    },
    {
      title: 'Mã đơn hàng',
      dataIndex: ['id', 'order_id'],
      key: 'order_id',
      minWidth: 100,
      width: 200,
      render: (_, { order_id }) => <Text className="text-[#1677FF]">{order_id}</Text>,
    },
    {
      title: 'Khách hàng',
      dataIndex: 'customer_name',
      key: 'customer_id',
      minWidth: 100,
      width: 200,
      render: (customer_name) => <Text className="text-[#667085]">{customer_name}</Text>,
    },
    {
      title: 'Miniapp',
      dataIndex: 'miniapp_name',
      key: 'miniapp_id',
      minWidth: 100,
      width: 200,
    },
    {
      title: 'Thông tin đơn hàng',
      dataIndex: 'order_info',
      key: 'order_info',
      className: 'max-w-[600px]',
    },
    {
      title: 'Ngày tạo đơn hàng',
      dataIndex: 'created_order_at',
      key: 'created_order_at',
      minWidth: 80,
      width: 200,
    },
    {
      title: 'Ngày ghi nhận',
      dataIndex: 'create_time',
      key: 'create_time',
      minWidth: 80,
      width: 200,
    },
    {
      title: 'Cập nhật gần nhất',
      dataIndex: 'update_time',
      key: 'update_time',
      minWidth: 80,
      width: 200,
    },
    {
      title: 'Tổng tiền',
      dataIndex: 'price',
      key: 'price',
      minWidth: 100,
      width: 200,
      render: (price) => <Text className="text-[#667085]">{formatPrice(price)} đ</Text>,
    },

    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      minWidth: 100,
      width: 200,
      render: (status) => (
        <Tag
          className="px-2 py-1"
          color={AppSelect.ORDER_STATUS?.find((item) => item?.value === status)?.color || '#FF9500'}
        >
          {status}
        </Tag>
      ),
    },
  ];

  return (
    <div className="m-5">
      <div className="flex justify-between items-center mb-5">
        <h1 className="text-2xl">
          Đối soát với {dataPartners?.data?.find((item) => item?.id === Number(partnerId))?.title}
        </h1>
        {/* <Link to={'/quan-ly-mini-apps/them-mini-app'}>
          <Button type="primary">
            <PlusOutlined />
            Thêm miniapp
          </Button>
        </Link> */}
      </div>

      <Form
        name="search_reconcile"
        form={formSearch}
        layout="vertical"
        requiredMark={false}
        className="mb-5"
        onFinish={handleSubmitForm}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item
              name="from_to_date"
              label={<Text>Thời gian</Text>}
              initialValue={[dayjs(params?.start_time), dayjs(params?.end_time)]}
            >
              <DatePicker.RangePicker
                placeholder={['Từ ngày', 'Đến ngày']}
                disabledDate={(current) => {
                  // Can not select days after
                  return current && current.valueOf() >= Date.now();
                }}
                allowClear={false}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="order_id" label={<Text>Mã đơn hàng</Text>}>
              <Input placeholder="Nhập mã đơn hàng" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="customer_name" label={<Text>Khách hàng</Text>}>
              <Input placeholder="Nhập tên khách hàng" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="miniapp_id" label={<Text>Miniapp</Text>} initialValue={-1}>
              <Select
                showSearch
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={dataMiniAppsOption}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="status" label={<Text>Trạng thái đơn hàng</Text>} initialValue="">
              <Select
                showSearch
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={[{ value: '', label: 'Tất cả' }, ...AppSelect.ORDER_STATUS]}
              />
            </Form.Item>
          </Col>
          <Col className="flex items-center gap-2">
            <Button type="primary" htmlType="submit">
              <SearchOutlined />
              Tìm kiếm
            </Button>
            {params &&
              Object.values(newObjectKeys(params, ['order_id', 'customer_name', 'miniapp_id', 'status'])).some(
                (value) => value
              ) && (
                <Button
                  htmlType="button"
                  onClick={() => {
                    formSearch.resetFields();
                    setParams({
                      page: 1,
                      limit: params.limit,
                      partner_id: [Number(partnerId)],
                      start_time: dayjs().subtract(3, 'months').format('YYYY-MM-DD'),
                      end_time: dayjs().format('YYYY-MM-DD'),
                    });
                  }}
                >
                  <DeleteOutlined />
                  Xóa lọc
                </Button>
              )}
          </Col>
        </Row>
      </Form>

      <TableCustom
        columns={columns}
        dataSource={dataReconciliations?.data}
        totalItem={dataReconciliations?.total}
        params={params}
        setParams={setParams}
      />
    </div>
  );
};

export default Reconciliation;
